/**
 * Token Refresh Interceptor + shared session machinery.
 *
 * One refresh flow for the whole app: axios instances attach via
 * setupTokenRefreshInterceptor; fetch-based clients use utils/authedFetch
 * (which calls the same refreshAccessToken). Guarantees:
 *  - 401 on an authed endpoint → refresh once (deduped app-wide) → retry
 *  - refresh success  → 'tend:token-refreshed' (AuthContext syncs state)
 *  - session truly dead → 'tend:session-expired' (AuthContext clears user;
 *    route guards bounce with returnUrl; ProfileSheet opens the sign-in
 *    sheet when already on '/'). No hard page reloads.
 *  - proxied non-auth 401s (e.g. content_service API-key failures surface
 *    as bare 401 "Unauthorized" with no WWW-Authenticate) never trigger
 *    refresh or signout — they are backend config errors, not session
 *    expiry.
 */

import { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import * as authApi from '../api/authApi';

// Track if a refresh is in progress to avoid multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];
let hasSignaledExpiry = false; // one expiry broadcast per dead session
let lastSuccessfulLoginTime = 0; // Timestamp of last successful login

// Minimum time after login before allowing an expiry signal (race guard)
const LOGIN_GRACE_PERIOD_MS = 5000;

export const TOKEN_REFRESHED_EVENT = 'tend:token-refreshed';
export const SESSION_EXPIRED_EVENT = 'tend:session-expired';

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

/**
 * Endpoints that MINT or BURN sessions — refreshing on their 401s would
 * loop or mask real failures. Everything else under /auth/ (notably
 * /auth/me, which AuthContext uses to hydrate sessions) IS refreshable:
 * the old blanket '/auth/' exclusion made a cold load with an expired
 * access token unrecoverable, and checkAuth then deleted BOTH tokens —
 * the "reload after 30 minutes = logged out" bug.
 */
const SESSION_ENDPOINTS = ['/auth/refresh', '/auth/login', '/auth/token', '/auth/register', '/auth/logout'];
export const isSessionEndpoint = (url: string): boolean =>
  SESSION_ENDPOINTS.some((e) => url.includes(e));

/**
 * Gateway auth 401s carry WWW-Authenticate: Bearer and a known detail.
 * Proxied 401s (downstream API-key mismatches re-raised verbatim) don't —
 * refreshing on those would "succeed", retry, 401 again, and wrongly kill
 * the session over a backend config problem.
 */
const AUTH_401_DETAILS = new Set([
  'Could not validate credentials',
  'Invalid authentication credentials',
  'Token is invalid or expired',
  'Not authenticated',
  'Invalid token type',
  'Refresh token is invalid or expired',
  'User not found',
  'Inactive user',
]);
export const isAuthShaped401 = (wwwAuthenticate: string | null | undefined, detail: unknown): boolean => {
  if (wwwAuthenticate && String(wwwAuthenticate).toLowerCase().includes('bearer')) return true;
  return typeof detail === 'string' && AUTH_401_DETAILS.has(detail);
};

/**
 * The session is dead: clear tokens, broadcast once. AuthContext owns the
 * state flip (user → null), which lets the route guards do a SOFT bounce
 * to the sign-in sheet with a returnUrl — no window.location reload.
 */
export const signalSessionExpired = () => {
  if (hasSignaledExpiry) return;
  const timeSinceLogin = Date.now() - lastSuccessfulLoginTime;
  if (timeSinceLogin < LOGIN_GRACE_PERIOD_MS) return;

  hasSignaledExpiry = true;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

/** Reset the expiry/refresh state (after successful login or on login page) */
export const resetRedirectFlag = () => {
  hasSignaledExpiry = false;
  isRefreshing = false;
  refreshSubscribers = [];
};

/** Mark the timestamp of a successful login (prevents expiry race conditions) */
export const markSuccessfulLogin = () => {
  lastSuccessfulLoginTime = Date.now();
  hasSignaledExpiry = false;
  isRefreshing = false;
  refreshSubscribers = [];
};

// Refresh proactively when the access token has less than this long to live.
export const TOKEN_REFRESH_MARGIN_MS = 60_000;

/**
 * Milliseconds until the stored access token expires, or null when there is
 * no token / no readable exp claim (in which case the reactive path decides).
 */
export const getAccessTokenRemainingMs = (): number | null => {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.exp !== 'number') return null;
    return payload.exp * 1000 - Date.now();
  } catch {
    return null;
  }
};

/**
 * Refresh the access token, deduped app-wide: concurrent callers (axios AND
 * authedFetch) share one refresh. On success, broadcasts the new token so
 * AuthContext can sync its state (context-token consumers would otherwise
 * keep sending the dead token forever). Throws on failure; when the failure
 * is DEFINITIVE (no refresh token, or the refresh endpoint said 401), the
 * session-expired broadcast fires — network blips do not kill the session.
 */
export const refreshAccessToken = async (): Promise<string> => {
  if (isRefreshing) {
    return new Promise((resolve) => subscribeTokenRefresh(resolve));
  }
  isRefreshing = true;
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      signalSessionExpired();
      throw new Error('No refresh token available');
    }
    const response = await authApi.refreshToken(refreshToken);
    localStorage.setItem('access_token', response.access_token);
    if (response.refresh_token) {
      localStorage.setItem('refresh_token', response.refresh_token);
    }
    onTokenRefreshed(response.access_token);
    window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: { token: response.access_token } }));
    return response.access_token;
  } catch (err) {
    onTokenRefreshed(localStorage.getItem('access_token') || '');
    const status = (err as AxiosError)?.response?.status;
    if (status === 401) {
      // The refresh token itself is dead/revoked — the one true expiry
      signalSessionExpired();
    }
    throw err;
  } finally {
    isRefreshing = false;
  }
};

/**
 * Setup automatic token refresh interceptor for an axios instance
 */
export const setupTokenRefreshInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry) {
        // Session-minting endpoints own their failures (a failed LOGIN must
        // not trigger refresh); check baseURL + url — the auth client
        // carries /api/auth in its baseURL.
        const fullUrl = `${originalRequest.baseURL || ''}${originalRequest.url || ''}`;
        if (isSessionEndpoint(fullUrl)) {
          return Promise.reject(error);
        }

        // Only refresh for GATEWAY auth 401s (see isAuthShaped401 docs)
        const detail = (error.response.data as any)?.detail;
        if (!isAuthShaped401(error.response.headers?.['www-authenticate'], detail)) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          const newToken = await refreshAccessToken();
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // refreshAccessToken already broadcast expiry when definitive
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  // Request interceptor: refresh proactively when the token is about to
  // expire (so requests never go out doomed to 401), then attach it.
  axiosInstance.interceptors.request.use(
    async (config) => {
      const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
      if (!isSessionEndpoint(fullUrl)) {
        const remaining = getAccessTokenRemainingMs();
        if (
          remaining !== null &&
          remaining < TOKEN_REFRESH_MARGIN_MS &&
          localStorage.getItem('refresh_token')
        ) {
          try {
            await refreshAccessToken();
          } catch {
            // Proceed with the stored token; the reactive 401 path owns
            // failure handling.
          }
        }
      }
      const token = localStorage.getItem('access_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

export default setupTokenRefreshInterceptor;
